import React, { useState, useRef, useEffect } from 'react';
import { toggleReaction } from '../api/reactions';
import '../styles/ReactionBar.css';

const DEFAULT_EMOJIS = ['👍', '❤️', '🔥', '💪', '🎉', '👏'];

const ReactionBar = ({ activityId, reactions = [], currentUserId, onReactionChange, allowedEmojis, onToggle }) => {
    const emojiOptions = allowedEmojis && allowedEmojis.length > 0 ? allowedEmojis : DEFAULT_EMOJIS;
    const [showPicker, setShowPicker] = useState(false);
    const [showTooltip, setShowTooltip] = useState(null);
    const [activePopover, setActivePopover] = useState(null);
    const pickerRef = useRef(null);
    const tooltipRef = useRef(null);
    const popoverRef = useRef(null);

    // Group reactions by emoji
    const grouped = {};
    for (const r of reactions) {
        if (!grouped[r.emoji]) grouped[r.emoji] = [];
        grouped[r.emoji].push(r);
    }

    // Close picker/tooltip/popover on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setShowPicker(false);
            }
            if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
                setShowTooltip(null);
            }
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setActivePopover(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggle = async (emoji, e) => {
        e.stopPropagation();
        try {
            if (onToggle) {
                await onToggle(emoji);
            } else {
                await toggleReaction(activityId, emoji);
            }
            setShowPicker(false);
            setActivePopover(null);
            if (onReactionChange) onReactionChange(activityId);
        } catch (err) {
            console.error('Failed to toggle reaction:', err);
        }
    };

    const handleChipClick = (emoji, e) => {
        e.stopPropagation();
        // On touch devices, show a popover instead of immediately toggling
        if ('ontouchstart' in window) {
            setActivePopover(activePopover === emoji ? null : emoji);
            setShowPicker(false);
        } else {
            handleToggle(emoji, e);
        }
    };

    const userReactedWith = (emoji) => {
        return (grouped[emoji] || []).some(r => r.user_id === currentUserId);
    };

    return (
        <div className="reaction-bar" onClick={(e) => e.stopPropagation()}>
            <div className="reaction-summary">
                {Object.entries(grouped).map(([emoji, users]) => (
                    <div key={emoji} className="reaction-group-wrapper">
                        <button
                            className={`reaction-chip ${userReactedWith(emoji) ? 'reacted' : ''}`}
                            onClick={(e) => handleChipClick(emoji, e)}
                            onMouseEnter={() => setShowTooltip(emoji)}
                            onMouseLeave={() => setShowTooltip(null)}
                        >
                            <span className="reaction-emoji">{emoji}</span>
                            <span className="reaction-count">{users.length}</span>
                        </button>
                        {showTooltip === emoji && !activePopover && (
                            <div className="reaction-tooltip" ref={tooltipRef}>
                                {users.map((u, i) => (
                                    <span key={u.id || i}>{u.username}</span>
                                ))}
                            </div>
                        )}
                        {activePopover === emoji && (
                            <div className="reaction-popover" ref={popoverRef}>
                                <div className="popover-users">
                                    {users.map((u, i) => (
                                        <span key={u.id || i} className="popover-username">{u.username}</span>
                                    ))}
                                </div>
                                <button
                                    className={`popover-react-btn ${userReactedWith(emoji) ? 'remove' : ''}`}
                                    onClick={(e) => handleToggle(emoji, e)}
                                >
                                    {userReactedWith(emoji) ? `Remove ${emoji}` : `React ${emoji}`}
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="reaction-add-wrapper" ref={pickerRef}>
                <button
                    className="reaction-add-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowPicker(!showPicker);
                    }}
                    title="Add reaction"
                >
                    +
                </button>
                {showPicker && (
                    <div className="reaction-picker">
                        {emojiOptions.map(emoji => (
                            <button
                                key={emoji}
                                className={`picker-emoji ${userReactedWith(emoji) ? 'already-reacted' : ''}`}
                                onClick={(e) => handleToggle(emoji, e)}
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReactionBar;
